import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiMail, FiLock, FiEye, FiEyeOff,
  FiShield, FiBox, FiCreditCard,
} from 'react-icons/fi';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { googleAuth } from '../services/api';
import './LoginPage.css';

const ROLE_REDIRECT = {
  admin:  '/admin/dashboard',
  vendor: '/vendor/dashboard',
  buyer:  '/',
};

const DEMO_ACCOUNTS = [
  { label: 'Buyer Demo',  email: 'buyer@demo.com',  password: 'demo123' },
  { label: 'Vendor Demo', email: 'vendor@demo.com', password: 'demo123' },
  { label: 'Admin Demo',  email: 'admin@demo.com',  password: 'demo123' },
];

const FEATURES = [
  { icon: <FiShield size={18} />,  text: 'Secure Payments' },
  { icon: <FiBox size={18} />,     text: '50K+ Products' },
  { icon: <FiCreditCard size={18} />, text: '500+ Vendors' },
];

const TESTIMONIAL = {
  quote: '"Fivora transformed my online shopping. Best deals, fastest delivery, and I trust every vendor here."',
  name:  'Ayesha Malik',
  role:  'Verified Buyer',
  initials: 'AM',
};

export default function LoginPage() {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || null;

  const [form, setForm] = useState({ email: '', password: '', remember: false });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [errors, setErrors] = useState({});

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (res) => {
      setSocialLoading('google');
      try {
        const { data } = await googleAuth({ accessToken: res.access_token });
        if (data.token) {
          localStorage.setItem('token', data.token);
          window.location.href = ROLE_REDIRECT[data.user.role] || '/';
        }
      } catch {
        toast.error('Google login failed. Please try again.');
      } finally {
        setSocialLoading('');
      }
    },
    onError: () => toast.error('Google login failed.'),
  });

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: '' }));
  };

  const fillDemo = (account) => {
    setForm((f) => ({ ...f, email: account.email, password: account.password }));
    setErrors({});
  };

  const validate = () => {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address';
    if (!form.password) errs.password = 'Password is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const data = await loginUser({ email: form.email.trim(), password: form.password });
      toast.success(`Welcome back, ${data.user.name.split(' ')[0]}!`);
      const dest = from?.pathname || ROLE_REDIRECT[data.user.role] || '/';
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Login failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-page">

      {/* ── Left Panel ──────────────────────────────────────────────────────── */}
      <div className="lp-left">
        <div className="lp-left-inner">

          {/* Logo */}
          <div className="lp-logo">
            <img src="/fivora-logo.png" alt="Fivora" className="lp-logo-img" />
            <div className="lp-logo-text">Fivora</div>
          </div>

          {/* Tagline */}
          <div className="lp-tagline-block">
            <h2 className="lp-tagline">Pakistan's Premier Marketplace</h2>
            <p className="lp-tagline-sub">
              Connect with hundreds of verified vendors and discover thousands of authentic products — all in one place.
            </p>
          </div>

          {/* Feature bullets */}
          <ul className="lp-features">
            {FEATURES.map(({ icon, text }) => (
              <li key={text} className="lp-feature-item">
                <span className="lp-feature-icon">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>

          {/* Testimonial */}
          <div className="lp-testimonial">
            <p className="lp-testimonial-quote">{TESTIMONIAL.quote}</p>
            <div className="lp-testimonial-author">
              <div className="lp-testimonial-avatar">{TESTIMONIAL.initials}</div>
              <div>
                <p className="lp-testimonial-name">{TESTIMONIAL.name}</p>
                <p className="lp-testimonial-role">{TESTIMONIAL.role}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative blobs */}
        <div className="lp-blob lp-blob-1" aria-hidden="true" />
        <div className="lp-blob lp-blob-2" aria-hidden="true" />
      </div>

      {/* ── Right Panel ─────────────────────────────────────────────────────── */}
      <div className="lp-right">
        <motion.div
          className="lp-form-wrap"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          {/* Heading */}
          <div className="lp-form-header">
            <h1 className="lp-form-title">Welcome Back</h1>
            <p className="lp-form-sub">Sign in to your Fivora account to continue</p>
          </div>

          {/* Form */}
          <form className="lp-form" onSubmit={handleSubmit} noValidate>

            {/* Email */}
            <div className="form-group">
              <label className="form-label" htmlFor="lp-email">Email Address</label>
              <div className="lp-input-wrap">
                <FiMail className="lp-input-icon" aria-hidden="true" />
                <input
                  id="lp-email"
                  type="email"
                  className={`form-input lp-input${errors.email ? ' error' : ''}`}
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>

            {/* Password */}
            <div className="form-group">
              <div className="lp-pass-label-row">
                <label className="form-label" htmlFor="lp-password">Password</label>
                <button type="button" className="lp-forgot-btn">Forgot password?</button>
              </div>
              <div className="lp-input-wrap">
                <FiLock className="lp-input-icon" aria-hidden="true" />
                <input
                  id="lp-password"
                  type={showPass ? 'text' : 'password'}
                  className={`form-input lp-input lp-input-pr${errors.password ? ' error' : ''}`}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={set('password')}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lp-toggle-pass"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>

            {/* Remember me */}
            <label className="lp-remember">
              <input
                type="checkbox"
                className="lp-checkbox"
                checked={form.remember}
                onChange={set('remember')}
              />
              <span className="lp-checkbox-box" />
              <span className="lp-remember-text">Remember me for 30 days</span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary btn-block lp-submit-btn"
              disabled={loading}
            >
              {loading
                ? <><span className="spinner spinner-sm" /> Signing in…</>
                : 'Sign In'}
            </button>
          </form>

          {/* Social login */}
          <div className="social-divider"><span>or continue with</span></div>
          <div className="social-buttons">
            <button
              type="button"
              className="social-btn social-btn-google"
              onClick={() => handleGoogleLogin()}
              disabled={!!socialLoading}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              {socialLoading === 'google' ? 'Connecting…' : 'Login with Google'}
            </button>
          </div>

          {/* Demo accounts */}
          <div className="lp-demo">
            <div className="lp-demo-label">
              <span>Try a demo account</span>
            </div>
            <div className="lp-demo-chips">
              {DEMO_ACCOUNTS.map((acc) => (
                <button
                  key={acc.label}
                  type="button"
                  className="lp-demo-chip"
                  onClick={() => fillDemo(acc)}
                >
                  {acc.label}
                </button>
              ))}
            </div>
          </div>

          {/* Register link */}
          <p className="lp-register-link">
            Don't have an account?{' '}
            <Link to="/register">Register</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
